/**
 * Official settlement for BTC 15m rounds, shared by every tracked history
 * (V1, V2/V3/V4, coin flip, dice, V1 + take-profit).
 *
 * Rounds used to be graded only against a Coinbase spot proxy: the round's
 * own 1-minute candle open vs. the Coinbase close near the round's end. The
 * contract actually settles on something else entirely — Kalshi's
 * `floor_strike` (a CF Benchmarks RTI average, not the Coinbase open) vs. the
 * RTI's 60-second average before close (`expiration_value`). On a close
 * round the two routinely disagree (e.g. round 1989625: proxy DOWN by
 * $10.01, official UP by $6.49), so the proxy result is now only
 * provisional and gets replaced by Kalshi's own `result` once it's
 * published.
 */

export type SettlementSource = 'kalshi_official' | 'coinbase_proxy';

export interface OfficialRoundResult {
  ticker: string;
  closeMs: number;
  /** The contract's target line (Kalshi `floor_strike`). */
  floorStrike: number;
  /** The RTI 60s average the contract settled on (Kalshi `expiration_value`). */
  expirationValue: number | null;
  actual: 'UP' | 'DOWN';
}

/** The fields every settleable history record shares. */
export interface SettleableRecord {
  roundCloseMs: number;
  action: 'UP' | 'DOWN';
  settled: boolean;
  settledMs: number | null;
  closePrice: number | null;
  actual: 'UP' | 'DOWN' | null;
  won: boolean | null;
  /** Absent on records written before official settlement existed — treated as proxy. */
  settlementSource?: SettlementSource;
  /** The Kalshi target line when known; absent/null falls back to `roundOpenPrice`. */
  targetLine?: number | null;
}

/**
 * The provisional (proxy) outcome: Coinbase close vs. the contract's own
 * target line when it was captured from the live quote, else the round's
 * Coinbase open. `>=` matches the contract's `strike_type:
 * "greater_or_equal"`, so a tie is UP.
 */
export function proxyActual(
  closePrice: number,
  record: { targetLine?: number | null; roundOpenPrice: number },
): 'UP' | 'DOWN' {
  const line = record.targetLine ?? record.roundOpenPrice;
  return closePrice >= line ? 'UP' : 'DOWN';
}

export function isOfficiallySettled(record: SettleableRecord): boolean {
  return record.settled && record.settlementSource === 'kalshi_official';
}

/** Indexes results by close time — the join key every history record already carries. */
export function indexByCloseMs(
  results: OfficialRoundResult[],
): Map<number, OfficialRoundResult> {
  return new Map(results.map((r) => [r.closeMs, r]));
}

/**
 * Oldest close time among closed rounds that still lack an official result
 * (unsettled, or settled only by the proxy), or null if there are none.
 * `skip` lets a caller exclude records whose outcome doesn't depend on
 * settlement (a take-profit early exit).
 */
export function oldestUnofficialCloseMs<T extends SettleableRecord>(
  records: T[],
  nowMs: number,
  skip: (record: T) => boolean = () => false,
): number | null {
  let oldest: number | null = null;
  for (const record of records) {
    if (record.roundCloseMs > nowMs || skip(record) || isOfficiallySettled(record)) {
      continue;
    }
    if (oldest === null || record.roundCloseMs < oldest) oldest = record.roundCloseMs;
  }
  return oldest;
}

/**
 * Re-grades every record with a matching official result, overriding any
 * earlier proxy settlement. Idempotent: already-official records are left
 * untouched. Returns the same array reference when nothing changed.
 */
export function applyOfficialResultsTo<T extends SettleableRecord>(
  records: T[],
  results: Map<number, OfficialRoundResult>,
  nowMs: number,
  skip: (record: T) => boolean = () => false,
): T[] {
  let changed = false;
  const next = records.map((record) => {
    if (skip(record) || isOfficiallySettled(record)) return record;
    const official = results.get(record.roundCloseMs);
    if (!official) return record;
    changed = true;
    return {
      ...record,
      settled: true,
      settledMs: record.settledMs ?? nowMs,
      closePrice: official.expirationValue ?? record.closePrice,
      targetLine: official.floorStrike,
      actual: official.actual,
      won: record.action === official.actual,
      settlementSource: 'kalshi_official' as const,
    };
  });
  return changed ? next : records;
}

/**
 * Merge preference shared by every history's merge: an incoming record
 * replaces the existing one when it's settled and the existing one isn't,
 * or when it carries the official result and the existing one doesn't —
 * so a browser's locally proxy-settled copy gets corrected by the server's.
 */
export function incomingSupersedes(
  current: SettleableRecord,
  incoming: SettleableRecord,
): boolean {
  if (!current.settled && incoming.settled) return true;
  return isOfficiallySettled(incoming) && !isOfficiallySettled(current);
}
